import { Link, NavLink, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { BrandMark } from "./Brand";

const MIDAS_URL = "https://www.getmidas.com";

const navItems = [
  { to: "/", label: "Ana Sayfa", end: true },
  { to: "/araci-kurumlar", label: "Tüm Aracı Kurumlar" },
  { to: "/borsa-haberleri", label: "Borsa Haberleri" },
  { to: "/hakkimizda", label: "Hakkımızda" },
];

const todayTR = () =>
  new Date().toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

const Header = () => {
  const [open, setOpen] = useState(false);
  useLocation();

  return (
    <header className="sticky top-0 z-50 border-b border-ink bg-paper" data-testid="site-header">
      {/* Üst künye şeridi */}
      <div className="hidden border-b border-hair sm:block">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-1.5">
          <span className="kicker text-muted-foreground">{todayTR()}</span>
          <span className="kicker text-muted-foreground">Bağımsız Aracı Kurum Rehberi · İstanbul</span>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex h-[74px] items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
            <BrandMark size={44} className="transition-transform group-hover:rotate-[8deg]" />
            <div className="leading-none">
              <div className="font-display text-[22px] font-semibold tracking-tight text-ink">
                SPK Aracı Kurumlar
              </div>
              <div className="kicker mt-1 text-claret">spkaracikurumlar.com</div>
            </div>
          </Link>

          <nav className="hidden items-center gap-7 lg:flex" data-testid="desktop-nav">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `relative py-1 text-sm font-medium transition-colors after:absolute after:-bottom-0.5 after:left-0 after:h-[2px] after:bg-claret after:transition-all ${
                    isActive
                      ? "text-ink after:w-full"
                      : "text-muted-foreground hover:text-ink after:w-0 hover:after:w-full"
                  }`
                }
                data-testid={`nav-${item.to === "/" ? "home" : item.to.slice(1)}`}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={MIDAS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden items-center gap-1.5 rounded-sm bg-ink px-5 py-2.5 text-sm font-semibold text-paper transition-colors hover:bg-claret sm:inline-flex"
              data-testid="open-account-midas-button"
            >
              Hesap Aç <ArrowUpRight size={16} />
            </a>
            <button
              className="rounded-sm p-2 text-ink hover:bg-tint lg:hidden"
              onClick={() => setOpen((o) => !o)}
              aria-label="Menü"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="border-t border-hair bg-paper px-4 py-3 lg:hidden" data-testid="mobile-nav">
          <div className="flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `rounded-sm px-4 py-2.5 text-sm font-medium ${isActive ? "bg-tint text-claret" : "text-ink"}`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <a
              href={MIDAS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-sm bg-ink px-5 py-3 text-sm font-semibold text-paper"
              data-testid="open-account-midas-button-mobile"
            >
              Hesap Aç <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

const Footer = () => (
  <footer className="mt-24 border-t-2 border-ink bg-paper" data-testid="site-footer">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-14">
      <div className="grid gap-10 md:grid-cols-3">
        <div>
          <div className="flex items-center gap-2.5">
            <BrandMark size={34} />
            <span className="font-display text-lg font-semibold text-ink">SPK Aracı Kurumlar</span>
          </div>
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-muted-foreground">
            Türkiye'nin bağımsız SPK lisanslı aracı kurumlar rehberi. İletişim bilgileri,
            güncel borsa haberleri ve kurum karşılaştırması.
          </p>
        </div>
        <div>
          <h4 className="kicker text-claret">Sayfalar</h4>
          <ul className="mt-4 space-y-2.5 text-sm text-muted-foreground">
            {navItems.map((n) => (
              <li key={n.to}>
                <Link to={n.to} className="transition-colors hover:text-ink">{n.label}</Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="kicker text-claret">Yasal Uyarı</h4>
          <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
            Sitemizde yer alan aracı kurumların iletişim numaraları ve kurumsal bilgileri kamuya
            açık kaynaklardan (KAP, SPK duyuruları) ve bildirilen haberlerden derlenmiştir.
            İçerikler yatırım tavsiyesi niteliğinde değildir.
          </p>
        </div>
      </div>
      <div className="mt-12 border-t border-hair pt-6 text-xs text-muted-foreground">
        © {new Date().getFullYear()} spkaracikurumlar.com — Tüm hakları saklıdır.
      </div>
    </div>
  </footer>
);

export const Layout = ({ children }) => (
  <div className="App min-h-screen bg-background">
    <Header />
    <main>{children}</main>
    <Footer />
  </div>
);
